// src/pages/SheetAdd.js
import React, { useState } from "react";
import {
  Box,
  Alert,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { addDoc, collection, doc, serverTimestamp, writeBatch } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { useNavigate } from "react-router-dom";

import { db } from "../firebase-config";
import Form from "../components/Form";

export default function SheetAdd() {
  const navigate = useNavigate();
  const auth = getAuth();

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // sheet meta (always shown above the form)
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [visibility, setVisibility] = useState("public"); // "private" | "public"

  const handleSave = async (payload) => {
    setError("");
    setSaving(true);

    try {
      const user = auth.currentUser;
      if (!user) throw new Error("Not logged in.");

      if (!name.trim()) throw new Error("Name is required.");

      const { variables = [], sections = [], items = [], ...sheetFields } = payload || {};

      // create main sheet doc WITHOUT arrays
      const docRef = await addDoc(collection(db, "sheets"), {
        // meta fields from header
        name: name.trim(),
        description: description.trim(),
        visibility,

        // anything else coming from the Form (non-array fields)
        ...sheetFields,

        version: 1,
        authorUid: user.uid,

        status: "published",
        allowSuggestions: true,

        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        publishedAt: serverTimestamp(),

        roles: {
          owner: [user.uid],
          admin: [user.uid],
          editor: [user.uid],
          viewer: [],
        },

        counters: {
          pendingEdits: 0,
          approvedEdits: 0,
        },
      });

      // write subcollections
      const batch = writeBatch(db);
      const now = serverTimestamp();

      const writeCollection = (sub, rows) => {
        const colRef = collection(db, "sheets", docRef.id, sub);

        for (const row of rows || []) {
          if (!row) continue;

          const { id, ...data } = row;
          const rowRef =
            typeof id === "string" && id.length ? doc(colRef, id) : doc(colRef);

          batch.set(rowRef, {
            ...data,
            createdAt: now,
            updatedAt: now,
          });
        }
      };

      writeCollection("variables", variables);
      writeCollection("sections", sections);
      writeCollection("items", items);

      await batch.commit();

      navigate(`/sheet/${docRef.id}`);
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Stack spacing={2} sx={{ mb: 3 }}>
        <TextField
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          required
        />

        <TextField
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          multiline
          minRows={2}
        />

        <FormControl fullWidth>
          <InputLabel>Visibility</InputLabel>
          <Select
            value={visibility}
            label="Visibility"
            onChange={(e) => setVisibility(e.target.value)}
          >
            <MenuItem value="private">Private</MenuItem>
            <MenuItem value="public">Public</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      <Form onSave={handleSave} saving={saving} error={error} />
    </Box>
  );
}
